import { api } from '@/lib/api';

/** The four states of the connection (ALS-68), as the API names them. */
export const XERO_STATE = {
    synced: 'Synced',
    /** Xero could not be reached lately and is being asked again: the badge reads "Reconnecting…". */
    syncing: 'Syncing',
    disconnected: 'Disconnected',
    notLinked: 'NotLinked',
} as const;

export type XeroState = (typeof XERO_STATE)[keyof typeof XERO_STATE];

/** Why a linked connection is not synced, which decides how it recovers. */
export const XERO_PROBLEM = {
    /** Xero could not be asked: trying again may be enough. */
    unreachable: 'Unreachable',
    /** Xero said no: only an admin connecting again cures it. */
    refused: 'Refused',
} as const;

export type XeroProblem = (typeof XERO_PROBLEM)[keyof typeof XERO_PROBLEM];

/** Whether a payment recorded here can be recorded in Xero too (ALS-456), as the API names it. */
export const XERO_PAYMENT_PUSH = {
    /** Xero is off here, or was never connected: nothing would send it. */
    off: 'Off',
    /** The link was approved before payments could be recorded in Xero: an admin connects it again. */
    reconnect: 'Reconnect',
    on: 'On',
} as const;

export type XeroPaymentPush = (typeof XERO_PAYMENT_PUSH)[keyof typeof XERO_PAYMENT_PUSH];

export interface XeroConnection {
    state: XeroState;
    organisation: string | null;
    problem: XeroProblem | null;
    checkedOn: string | null;
    connectedOn: string | null;
    connectedBy: string | null;
    mayConnect: boolean;
    mayCheck: boolean;
    mayDisconnect: boolean;
    /** Since when Xero's webhook has stopped announcing changes, which the check now finds (ALS-70). */
    updatesMissedSince: string | null;
    /** Whether Record payment can record a payment in Xero too (ALS-456). */
    payments: XeroPaymentPush;
}

/** Where one of an order's documents stands with Xero (ALS-69), as the API names it. */
export const XERO_DOCUMENT_STATE = {
    sent: 'Sent',
    /** On its way: not taken up yet, or Xero could not be reached and it is tried again. */
    waiting: 'Waiting',
    /** Not in Xero, for a reason a person has to deal with; Retry sends it again. */
    failed: 'Failed',
    /** Not for the push: see the reason. */
    notSent: 'NotSent',
} as const;

export type XeroDocumentState = (typeof XERO_DOCUMENT_STATE)[keyof typeof XERO_DOCUMENT_STATE];

/** Why a document is not for the push. */
export const XERO_NOT_SENT = {
    /** Issued before Xero was first connected here: raised in Xero by hand, if at all. */
    beforeXero: 'BeforeXero',
    /** The order carries a Xero reference typed by hand. */
    byHand: 'ByHand',
} as const;

export type XeroNotSent = (typeof XERO_NOT_SENT)[keyof typeof XERO_NOT_SENT];

/** An order's documents, and a payment recorded here on its way to Xero (ALS-456), which the queue carries as one. */
export const XERO_DOCUMENT_KIND = {
    invoice: 'Invoice',
    creditNote: 'CreditNote',
    payment: 'Payment',
} as const;

export type XeroDocumentKind = (typeof XERO_DOCUMENT_KIND)[keyof typeof XERO_DOCUMENT_KIND];

/** One of an order's documents. `id` is null until the push takes it up, and for one not for the push. */
export interface XeroDocument {
    id: number | null;
    kind: XeroDocumentKind;
    number: string;
    creditNoteId: number | null;
    state: XeroDocumentState;
    notSent: XeroNotSent | null;
    /** The Xero reference typed by hand, for an invoice raised in Xero that way. */
    reference: string | null;
    problem: string | null;
    /** When the push tries a waiting document again. */
    retryAt: string | null;
    sentOn: string | null;
    mayRetry: boolean;
    /** For an invoice in Xero: when its payments were last read back (ALS-70). */
    readOn: string | null;
    /** For an invoice in Xero: where Xero's invoice differs from the admin's, which a person settles. */
    conflict: string | null;
    /** For a payment: the payment recorded here it sends (ALS-456), which Payments recorded lists by this id. */
    orderPaymentId: number | null;
    /**
     * Who dismissed all of that on the daily check of the books, when and why (ALS-388); null while
     * any of it is still open there, or nothing differs.
     */
    conflictDismissed?: { reason: string; by: string; at: string } | null;
}

/** Where one use of wallet credit on an order stands in Xero (ALS-384), as the API names it. */
export const CREDIT_USE_STATE = {
    /** On its way: the push has not settled it yet. */
    waiting: 'Waiting',
    allocated: 'Allocated',
    /** Left to the accountant, for the reason given, until someone marks it done. */
    byHand: 'ByHand',
    /** Xero holds less of the note than expected, or the invoice owes less than the credit. */
    outOfSync: 'OutOfSync',
    /** Xero refused it, or its credit note did not reach Xero; Retry sends the note again. */
    failed: 'Failed',
    /** Settled in Xero by hand, and marked so. */
    done: 'Done',
} as const;

export type CreditUseState = (typeof CREDIT_USE_STATE)[keyof typeof CREDIT_USE_STATE];

/** One use of wallet credit on an order (ALS-384). Amounts in pounds, and in the order's currency. */
export interface CreditUse {
    id: number;
    orderId: number;
    orderNo: string;
    on: string;
    /** The credit note it came off; null for credit no credit note backs. */
    creditNote: string | null;
    /** Pounds, as the wallet and every credit note are. */
    amount: number;
    /** What came off the invoice, in the order's currency. */
    orderAmount: number;
    currency: string;
    /** Units of the order's currency to one pound. */
    rate: number;
    invoiceNo: string;
    state: CreditUseState;
    problem: string | null;
    /** The credit note's own document, which Retry sends again. */
    retryDocumentId: number | null;
    mayRetry: boolean;
    mayMarkDone: boolean;
    doneBy: string | null;
    doneOn: string | null;
}

export interface OrderXero {
    invoice: XeroDocument | null;
    creditNotes: XeroDocument[];
    /** The wallet credit used on the order, oldest first, and where each use stands (ALS-384). */
    credit: CreditUse[];
    /** The payments recorded here that were sent to Xero, oldest first, and where each stands (ALS-456). */
    payments: XeroDocument[];
}

/** A top-up's credit note (ALS-384), against the passbook row it backs. */
export interface CustomerCreditNote {
    walletEntryId: number;
    document: XeroDocument;
}

/** The client's wallet in Xero: their top-ups' credit notes, and credit Xero is out of sync with. */
export interface CustomerXero {
    creditNotes: CustomerCreditNote[];
    outOfSync: CreditUse[];
}

/** What Retry came to. */
export const XERO_PUSH_OUTCOME = {
    sent: 'Sent',
    /** It was in Xero already: nothing was sent. */
    alreadySent: 'AlreadySent',
    waiting: 'Waiting',
    failed: 'Failed',
    /** A credit note in Xero whose credit could not all be applied there (ALS-486). */
    notApplied: 'NotApplied',
    /** A credit note in Xero whose credit waits for an invoice to reach Xero first (ALS-486). */
    notAppliedYet: 'NotAppliedYet',
    /** Another attempt is sending it right now. */
    busy: 'Busy',
} as const;

export type XeroPushOutcome = (typeof XERO_PUSH_OUTCOME)[keyof typeof XERO_PUSH_OUTCOME];

export interface XeroPushAnswer {
    outcome: XeroPushOutcome;
    problem: string | null;
    /** The order's documents after it; null for a top-up's credit note, which is no order's (ALS-384). */
    order: OrderXero | null;
}

/** A sales account a product or an order line may take (ALS-386): the legacy row's id, which is what they store. */
export interface XeroAccountChoice {
    id: number;
    code: string;
    name: string;
    /** One the team uses daily, listed first. */
    daily: boolean;
}

/** A sales account's use on products and lines, set in the register. */
export const XERO_ACCOUNT_USE = { daily: 'Daily', offered: 'Offered', hidden: 'Hidden' } as const;

export type XeroAccountUse = (typeof XERO_ACCOUNT_USE)[keyof typeof XERO_ACCOUNT_USE];

export const XERO_ACCOUNT_USES = [
    XERO_ACCOUNT_USE.daily,
    XERO_ACCOUNT_USE.offered,
    XERO_ACCOUNT_USE.hidden,
] as const satisfies readonly XeroAccountUse[];

/** Where an account stands in Xero. */
export const XERO_ACCOUNT_STATUS = {
    active: 'Active',
    archived: 'Archived',
    gone: 'Gone',
} as const;

export type XeroAccountStatus = (typeof XERO_ACCOUNT_STATUS)[keyof typeof XERO_ACCOUNT_STATUS];

/** One account of Xero's chart as last read here. */
export interface XeroAccountRow {
    id: string;
    code: string | null;
    name: string;
    /** Xero's type: REVENUE, SALES, BANK, ... */
    type: string;
    sales: boolean;
    status: XeroAccountStatus;
    /** A sales account's use on lines; null for the rest of the chart. */
    use: XeroAccountUse | null;
    useRevisedBy: string | null;
    useRevisedOn: string | null;
}

/** What the register can say about an account or tax type the admin posts to without asking. */
export const XERO_CHECK = {
    ok: 'Ok',
    archived: 'Archived',
    missing: 'Missing',
    unmapped: 'Unmapped',
    notRead: 'NotRead',
} as const;

export type XeroCheck = (typeof XERO_CHECK)[keyof typeof XERO_CHECK];

/** The accounts in settings: Goods Sold for a product with none, and each charge's own. */
export const XERO_CHARGE = {
    products: 'Products',
    aListFee: 'AListFee',
    cardFee: 'CardFee',
    duties: 'Duties',
    shipping: 'Shipping',
} as const;

export type XeroCharge = (typeof XERO_CHARGE)[keyof typeof XERO_CHARGE];

export interface XeroChargeCheck {
    charge: XeroCharge;
    code: string;
    name: string | null;
    state: XeroCheck;
}

export interface XeroTaxCheck {
    taxId: number;
    name: string;
    rate: number;
    taxType: string | null;
    xeroName: string | null;
    state: XeroCheck;
}

/** The register: when Xero's chart was last read and from which organisation (null before the first read). */
export interface XeroAccounts {
    readOnUtc: string | null;
    organisation: string | null;
    /** Whether Xero is linked here, which a refresh needs. */
    linked: boolean;
    /** Whether this person may refresh the chart and set what lines are offered. */
    mayManage: boolean;
    accounts: XeroAccountRow[];
    charges: XeroChargeCheck[];
    taxes: XeroTaxCheck[];
}

/** Why an order shows no payment link (ALS-385), as the API names it. */
export const PAYMENT_LINK_ABSENCE = {
    /** Not an order for a link: paid by bank transfer, cancelled or deleted. Nothing is shown. */
    notOffered: 'NotOffered',
    nothingToPay: 'NothingToPay',
    /** A plan whose instalment due now is by bank transfer. */
    transferDue: 'TransferDue',
    noInvoice: 'NoInvoice',
    /** The invoice is on its way to Xero. */
    sending: 'Sending',
    notInXero: 'NotInXero',
    /** Issued before Xero was connected here: never pushed, and connecting would not change that. */
    issuedBeforeXero: 'IssuedBeforeXero',
    /** Raised in Xero by hand, so the admin does not know it there. */
    outsideAdmin: 'OutsideAdmin',
    notConnected: 'NotConnected',
    /** Xero could not be reached: Try again may be enough. */
    unreachable: 'Unreachable',
    voided: 'Voided',
    /** Xero's amount due is not the order's outstanding: the link is held until they agree; `charges` is Xero's. */
    disagrees: 'Disagrees',
    /** Xero has no link for the invoice; `detail` says what it said. */
    unavailable: 'Unavailable',
} as const;

export type PaymentLinkAbsence = (typeof PAYMENT_LINK_ABSENCE)[keyof typeof PAYMENT_LINK_ABSENCE];

/**
 * An order's payment link: Xero's own link to the invoice's online page, what Xero will charge
 * through it, and whether that takes in a bank-transfer instalment too; or why there is none.
 */
export interface OrderPaymentLink {
    url: string | null;
    /** What is still due on the invoice in Xero, which the link charges; held, what it would have charged. */
    charges: number | null;
    currency: string | null;
    includesTransfer: boolean;
    absent: PaymentLinkAbsence | null;
    detail: string | null;
}

export const xeroApi = {
    /** The state, which the API checks with Xero first when its last answer is old. */
    connection: () => api<XeroConnection>('/api/xero/connection'),
    /** Try again: asks Xero now. */
    check: () => api<XeroConnection>('/api/xero/connection/check', { method: 'POST' }),
    /** Where to send the admin to approve the app; Xero brings them back to `returnTo`. */
    connect: (returnTo: string) =>
        api<{ url: string }>('/api/xero/connect', { method: 'POST', body: { returnTo } }),
    disconnect: () => api<XeroConnection>('/api/xero/connection', { method: 'DELETE' }),
    /** An order's invoice and credit notes, and where each stands with Xero. */
    orderDocuments: (orderId: number) => api<OrderXero>(`/api/orders/${orderId}/xero`),
    /** Xero's payment link on a card order, or why it has none (ALS-385). */
    paymentLink: (orderId: number) => api<OrderPaymentLink>(`/api/orders/${orderId}/payment-link`),
    /** Retry: sends one document to Xero now. */
    push: (documentId: number) =>
        api<XeroPushAnswer>(`/api/xero/documents/${documentId}/push`, { method: 'POST' }),
    /** The chart of accounts as last read, and what the settings and tax rows name (ALS-386). */
    accounts: () => api<XeroAccounts>('/api/xero/accounts'),
    /** Refresh from Xero: reads the chart and the tax rates now. */
    refreshAccounts: () => api<XeroAccounts>('/api/xero/accounts/refresh', { method: 'POST' }),
    /** Sets a sales account's use on products and lines. */
    setAccountUse: (id: string, use: XeroAccountUse) =>
        api<XeroAccountRow>(`/api/xero/accounts/${id}/use`, { method: 'PUT', body: { use } }),
    /** Wallet credit settled in Xero by hand (ALS-384); answers the order's documents after it. */
    creditUseDone: (useId: number) =>
        api<OrderXero>(`/api/xero/credit-uses/${useId}/done`, { method: 'POST' }),
    /** A client's top-up credit notes and the credit Xero is out of sync with (ALS-384). */
    customer: (customerId: number) => api<CustomerXero>(`/api/customers/${customerId}/xero`),
};
